import type { Middleware } from 'redux';
import * as Sentry from '@sentry/react';

import { userLogout } from '@uncinc/react-drupal-core';
import { SET_LOGGED_IN, userGetCurrent } from '@uncinc/react-drupal-core/dist/redux/actions/Login';
import type { ImmutableRootState as CoreImmutableRootState } from '@uncinc/react-drupal-core/types';

import { gtm } from '@uncinc/uncinc-react-kitchen-sink';

import { IS_REACT_NATIVE } from '../../config/constants';
import { TypedDispatch } from '../../hooks/useTypedDispatch';
import { userRegisterPushNotificationToken, userRemoveAccount, userUnRegisterPushNotificationToken } from '../actions/User';
import { clearOpenedWithNotificationId } from '../actions/Notification';
import {
  OPEN_PUSH_NOTIFICATION_SETTINGS,
  REQUEST_REACT_NATIVE_DELETE_USER,
  REQUEST_REACT_NATIVE_LOGOUT,
  SHOW_APP_SWITCHER,
} from '../actions/UI';

const ACTION_LOGIN = 'LOGIN';
const ACTION_LOGOUT = 'LOGOUT';

type AppStateStatus =
  | 'active'
  | 'background'
  | 'inactive'
  | 'unknown'
  | 'extension';

declare global {
  interface Window {
    jellinekzelfhulp: {
      alert?: (message: string) => void
      appStateChange?: (state: AppStateStatus) => void
      registerFcmToken?: (token: string) => void
      handleLogout?: (token: string) => void
      handleDeleteUser?: (token: string) => void
    };

    ReactNativeWebView: {
      postMessage: (message: string) => void;
    }
  }
}

interface ImmutableRootState<T> extends CoreImmutableRootState<T> {
  get: (key: any, notSetValue?: T) => T;
}

interface ReduxMiddleWare extends Middleware<{}, ImmutableRootState<unknown>, TypedDispatch> {}

/**
 * Exposes some Redux actions for usage outside of the app.
 *
 * Used primarily for connecting the web app with the mobile app.
 */
const externalReduxMiddleware: ReduxMiddleWare = ({ dispatch, getState }) => (next) => (action) => {
  window.jellinekzelfhulp = window.jellinekzelfhulp || {};

  /**
   * Shows an alert, for debugging purposes.
   */
  window.jellinekzelfhulp.alert = (message) => {
    // eslint-disable-next-line no-alert
    alert(message);
  };

  /**
   * Receive app state changes to update user data.
   */
  window.jellinekzelfhulp.appStateChange = (state) => {
    if (state === 'active') {
      dispatch(userGetCurrent());
    }
  };

  /**
   * Enables registering a push notification token from outside of the app.
   */
  window.jellinekzelfhulp.registerFcmToken = (token) => {
    const uid = getState().getIn(['login', 'uid']);
    if (uid) {
      dispatch(userRegisterPushNotificationToken(token)).catch((error) => {
        Sentry.captureException(error, { tags: { section: 'registerFcmToken' } });
      });
    }
  };

  /**
   * Enables removing a push notification token from outside of the app and logging out.
   */
  window.jellinekzelfhulp.handleLogout = (token) => {
    const uid = getState().getIn(['login', 'uid']);
    if (uid) {
      dispatch(clearOpenedWithNotificationId());
      dispatch(userUnRegisterPushNotificationToken(token)).then(() => {
        dispatch(userLogout());
      }).catch((error) => {
        Sentry.captureException(error, { tags: { section: 'handleLogout' } });
      });
    }
  };

  /**
   * Enables removing a push notification token from outside of the app
   * and removing the user and logging out.
   */
  window.jellinekzelfhulp.handleDeleteUser = (token) => {
    const uid = getState().getIn(['login', 'uid']);
    if (uid) {
      dispatch(userUnRegisterPushNotificationToken(token)).then(() => {
        dispatch(userLogout());
        dispatch(userRemoveAccount());
      }).catch((error) => {
        Sentry.captureException(error, { tags: { section: 'handleDeleteUser' } });
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  switch (action.type) {
    case SET_LOGGED_IN: {
      const isLoggedIn = getState().getIn(['login', 'loggedIn']);
      if (!IS_REACT_NATIVE) {
        // Ignore, we are not in React Native context.
        break;
      }

      // Pass the login state back to the mobile app via the
      // window.ReactNativeWebView.postMessage() function.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (action.data === true) {
        const uid = getState().getIn(['login', 'uid']);
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: ACTION_LOGIN,
          payload: { uid },
        }));

        // Push user login from app event to GTM.
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        gtm.trackUserEvent('user', 'user_login_from_app');
      } else if (isLoggedIn) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: ACTION_LOGOUT,
        }));
      }
      break;
    }
    case SHOW_APP_SWITCHER: {
      if (!IS_REACT_NATIVE) {
        // Ignore, we are not in React Native context.
        break;
      }

      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: SHOW_APP_SWITCHER,
      }));
      break;
    }
    case REQUEST_REACT_NATIVE_LOGOUT: {
      if (!IS_REACT_NATIVE) {
        // Ignore, we are not in React Native context.
        break;
      }
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: REQUEST_REACT_NATIVE_LOGOUT,
      }));
      break;
    }
    case REQUEST_REACT_NATIVE_DELETE_USER: {
      if (!IS_REACT_NATIVE) {
        // Ignore, we are not in React Native context.
        break;
      }
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: REQUEST_REACT_NATIVE_DELETE_USER,
      }));
      break;
    }
    case OPEN_PUSH_NOTIFICATION_SETTINGS: {
      if (!IS_REACT_NATIVE) {
        // Ignore, we are not in React Native context.
        break;
      }

      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: OPEN_PUSH_NOTIFICATION_SETTINGS,
      }));
      break;
    }
    default:
      break;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return next(action);
};

export default externalReduxMiddleware;
